import { commandBus } from '@/features/chat'
import { useStudioStore } from '@/store/studio-store'
import { useStudioLocale } from '@/i18n'
import { getDataSurfaceCopy } from '../../data-surface-copy'
import type { VariableEntity } from '../../hooks/use-entity-index'
import { useVariableUsageIndex } from '../../hooks/use-usage-index'

function selectVariableEntity(v: VariableEntity) {
  commandBus.dispatch({
    type: 'selectEntity',
    payload: { blockName: v.blockName, category: 'variables', name: v.name },
  })
  useStudioStore.getState().selectEntity(v.id)
}

export function VariableTable({ variables }: { variables: VariableEntity[] }) {
  const { locale, strings } = useStudioLocale()
  const copy = getDataSurfaceCopy(locale)
  const fields = strings.dataInspector.fields
  const varUsageIndex = useVariableUsageIndex()

  if (variables.length === 0) {
    return <p className="text-xs text-muted-foreground p-4">{copy.emptyTitle.variables}</p>
  }

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border text-left text-muted-foreground">
          <th className="px-3 py-2 font-medium">{fields.block}</th>
          <th className="px-3 py-2 font-medium">{fields.name}</th>
          <th className="px-3 py-2 font-medium">{fields.scope}</th>
          <th className="px-3 py-2 font-medium">{fields.type}</th>
          <th className="px-3 py-2 font-medium">{fields.defaultValue}</th>
          <th className="px-3 py-2 font-medium">{fields.usedBy}</th>
        </tr>
      </thead>
      <tbody>
        {variables.map((v) => {
          const usages = varUsageIndex.get(v.name) ?? []
          const usageNames = usages
            .map((u) => u.flowName ?? u.blockName)
            .filter(Boolean)
            .join(', ')

          return (
            <tr
              key={`${v.id}.${v.scope}`}
              className="border-b border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
              onClick={() => selectVariableEntity(v)}
            >
              <td className="px-3 py-1.5 text-muted-foreground">{v.blockName}</td>
              <td className="px-3 py-1.5 font-mono text-primary">{v.name}</td>
              <td className="px-3 py-1.5 text-muted-foreground">{v.scope}</td>
              <td className="px-3 py-1.5">
                <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{v.valueType}</span>
              </td>
              <td className="px-3 py-1.5 font-mono text-muted-foreground truncate max-w-[120px]">
                {v.defaultValue !== undefined ? String(v.defaultValue) : '—'}
              </td>
              <td className="px-3 py-1.5">
                {usages.length > 0 ? (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                    title={usageNames}
                  >
                    {copy.statusFlows(usages.length)}
                  </span>
                ) : (
                  <span className="text-muted-foreground/50 text-[10px]">{copy.unused}</span>
                )}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
